import { AppDispatch, AppGetState } from 'types/RootState.types';

// Config
import { getDefaultPaymentCardOrFirst } from '@redux/reducers/user/userReducer';
import { DETERMINE_SELECTED_PAYMENT_TYPE } from '@constants/actionConstants';
import paymentTypeConstants from '@constants/paymentTypeConstants';
import userConstants from '@constants/userConstants';
import isPayPalAvailable from '@utils/payment/isPayPalAvailable';
import { isEmptyObject } from '@utils/object';
import isZeroAmount from '@utils/payment/isZeroAmount';

//
import { setSelectedPaymentType } from './paymentActions';

export function canSelectGooglePay({
  canMakeGooglePayPayments = false,
  googlePayPaymentsClientHasBeenCreated = false,
}) {
  return canMakeGooglePayPayments && googlePayPaymentsClientHasBeenCreated;
}

export function paymentNoLongerEligible(isEligible = false, selectedPaymentType = '', paymentType = '') {
  return !isEligible && selectedPaymentType?.includes(paymentType);
}

export function posCreditNoLongerEligible(posCreditEligible = false, selectedPaymentType = '') {
  return paymentNoLongerEligible(posCreditEligible, selectedPaymentType, paymentTypeConstants.POS_CREDIT);
}

export function payPalPayLaterNoLongerEligible(payPalPayLaterEligible = false, selectedPaymentType = '') {
  return paymentNoLongerEligible(payPalPayLaterEligible, selectedPaymentType, paymentTypeConstants.PAYPAL_PAY_LATER);
}

export function klarnaNoLongerEligible(klarnaEligible = false, selectedPaymentType = '') {
  return paymentNoLongerEligible(klarnaEligible, selectedPaymentType, paymentTypeConstants.KLARNA);
}

export const checkPayLaterOptionsAvailable = () => (dispatch: AppDispatch, getState: AppGetState) => {
  const { selectedPaymentType, posCreditEligible } = getState().payment;

  let selectNewPaymentType = false;

  if (
    selectedPaymentType?.includes(paymentTypeConstants.POS_CREDIT) &&
    posCreditNoLongerEligible(posCreditEligible, selectedPaymentType)
  ) {
    selectNewPaymentType = true;
  }

  if (selectNewPaymentType) {
    return dispatch(setSelectedPaymentType());
  }

  return {};
};

export const priorityOrder = [
  paymentTypeConstants.APPLE_PAY,
  paymentTypeConstants.GOOGLE_PAY,
  paymentTypeConstants.SAVED_PAYMENT_CARD,
  paymentTypeConstants.CREDIT_CARD,
  paymentTypeConstants.PAYPAL,
  paymentTypeConstants.LINK,
];

export const getPayNowPriorityOrder = () => (_dispatch: AppDispatch, getState: AppGetState) => {
  const state = getState();
  const { user: { customerPreferences: { paymentChoice = '' } = {} } = {} } = state;

  const { APPLE_PAY, GOOGLE_PAY, LINK, PAYPAL, SAVED_PAYMENT_CARD } = paymentTypeConstants;

  const {
    PAYMENT_PREFERENCE_PAYPAL,
    PAYMENT_PREFERENCE_APPLE_PAY,
    PAYMENT_PREFERENCE_GOOGLE_PAY,
    PAYMENT_PREFERENCE_SAVED_CARD,
    PAYMENT_PREFERENCE_LINK,
  } = userConstants;

  if (
    [
      PAYMENT_PREFERENCE_PAYPAL,
      PAYMENT_PREFERENCE_APPLE_PAY,
      PAYMENT_PREFERENCE_GOOGLE_PAY,
      PAYMENT_PREFERENCE_SAVED_CARD,
      PAYMENT_PREFERENCE_LINK,
    ].includes(paymentChoice)
  ) {
    const lookup = new Map();
    lookup.set(PAYMENT_PREFERENCE_APPLE_PAY, APPLE_PAY);
    lookup.set(PAYMENT_PREFERENCE_GOOGLE_PAY, GOOGLE_PAY);
    lookup.set(PAYMENT_PREFERENCE_SAVED_CARD, SAVED_PAYMENT_CARD);
    lookup.set(PAYMENT_PREFERENCE_LINK, LINK);
    lookup.set(PAYMENT_PREFERENCE_PAYPAL, PAYPAL);

    // move the preferred payment method to the front of the list
    const mappedPreference = lookup.get(paymentChoice);
    const found = priorityOrder.indexOf(mappedPreference);
    priorityOrder.unshift(priorityOrder.splice(found, 1)[0]);
  }

  return priorityOrder;
};

export const determineSelectedPaymentType = () => async (dispatch: AppDispatch, getState: AppGetState) => {
  if (isZeroAmount(getState().orderForm?.amounts?.outstandingBalance)) {
    dispatch({
      type: DETERMINE_SELECTED_PAYMENT_TYPE,
      selectedPaymentType: undefined,
    });

    return;
  }

  await dispatch(checkPayLaterOptionsAvailable());

  const {
    applePayCompatibilityChecksComplete,
    selectedPaymentType,
    paymentWallet = [],
    canMakeGooglePayPayments,
    googlePayPaymentsClientHasBeenCreated,
  } = getState().payment;

  if (!selectedPaymentType) {
    let newSelectedPaymentType: string | undefined = undefined;

    const defaultSavedPaymentCard = getDefaultPaymentCardOrFirst(paymentWallet);

    const { APPLE_PAY, GOOGLE_PAY, SAVED_PAYMENT_CARD, CREDIT_CARD, PAYPAL } = paymentTypeConstants;

    const priorityOrder = dispatch(getPayNowPriorityOrder());

    priorityOrder.forEach((method: string) => {
      // Try and select each payment method in turn, setting newSelectedPaymentType if any succeed to stop trying others
      if (!newSelectedPaymentType) {
        if (method === APPLE_PAY && applePayCompatibilityChecksComplete) newSelectedPaymentType = method;
        if (
          method === GOOGLE_PAY &&
          canSelectGooglePay({
            canMakeGooglePayPayments,
            googlePayPaymentsClientHasBeenCreated,
          })
        )
          newSelectedPaymentType = method;
        if (method === SAVED_PAYMENT_CARD && defaultSavedPaymentCard)
          newSelectedPaymentType = `${SAVED_PAYMENT_CARD}:${defaultSavedPaymentCard.id}`;
        if (method === CREDIT_CARD && isEmptyObject(paymentWallet)) newSelectedPaymentType = CREDIT_CARD;
        if (method === PAYPAL && isPayPalAvailable(getState())) newSelectedPaymentType = method;
      }
    });

    if (newSelectedPaymentType) {
      dispatch({
        type: DETERMINE_SELECTED_PAYMENT_TYPE,
        selectedPaymentType: newSelectedPaymentType,
      });
    }
  }
};

export default determineSelectedPaymentType;
